import { cn } from '@/lib/cn';
import { summarizeThemeToken } from '@/lib/theme-token-summary';
import type { FontToken, Theme } from '@personalizehq/schemas/ir';
import { CONTROL, ToggleGroup } from '@/components/builder/inspector-controls';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Italic, Plus, Type } from 'lucide-react';
import { useState } from 'react';

// "LIKE `body`, BUT 18pt."
//
// `text.font` is a TOKEN KEY, so the type style comes as a bundle and a per-node size would change
// every block using that token. That is deliberate — type scale is where design systems actually rot,
// and unlike a colour there is nothing to derive: a one-off 18pt still needs a `line_height` and a
// `max_scale` decision, and getting `max_scale` wrong ships type that breaks at 2× Dynamic Type with
// nothing in the panel to say so.
//
// So rather than open the field to inline values, this makes the SECOND type style cheap. Same
// family, one field different, named after what changed, created and assigned in one click. The
// theme stays the complete inventory of type in the experience — which is what makes a brand review,
// the Theme studio, and a future MCP writer possible at all.
//
// FAMILY IS NOT HERE, on purpose: a different family is a different token, not a variant of this one.
// `max_scale` is inherited for the safety reason above.

/** Upright vs italic, as glyphs. `Type` is the panel's own "plain text" mark and `Italic` is the
 *  slanted serif every editor uses — a two-cell segmented control needs no words. */
const STYLES = [
  { value: 'normal', icon: Type, title: 'normal' },
  { value: 'italic', icon: Italic, title: 'italic' },
] as const;

export function FontVariantPopover({
  token,
  theme,
  onCreate,
}: {
  /** The token being varied. Absent (a dangling reference) means there is nothing to vary FROM. */
  token: string | null | undefined;
  theme: Theme;
  /** Create AND assign, in one `applyChange` — ⌘Z must undo "I used a new type style", never leave
   *  an orphan token behind. */
  onCreate: (next: FontToken) => void;
}): React.JSX.Element | null {
  const base = token ? theme.fonts[token] : undefined;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<FontToken | null>(null);

  // Nothing to derive from. The row still offers the picker and the Theme panel; this affordance
  // simply has no meaning without a base, and a disabled button that never explains itself is worse
  // than an absent one.
  if (!base) return null;

  const next = draft ?? base;
  const patch = (fields: Partial<FontToken>): void => setDraft({ ...next, ...fields });
  // Nothing changed means nothing to create — and saying so by disabling the button is clearer than
  // silently minting a duplicate of the token they already have.
  const changed =
    next.size !== base.size ||
    next.weight !== base.weight ||
    next.line_height !== base.line_height ||
    (next.style ?? 'normal') !== (base.style ?? 'normal') ||
    (next.letter_spacing ?? 0) !== (base.letter_spacing ?? 0);

  const close = (): void => {
    setDraft(null);
    setOpen(false);
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
      {/* `[data-popover]` so the inventory walker opens it and drives what is inside. */}
      <PopoverPrimitive.Trigger
        data-popover=""
        aria-label="new type style"
        title={`New type style from ${token}`}
        className="grid size-8 shrink-0 place-items-center rounded-lg border border-transparent bg-muted/70 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <Plus className="size-3.5" />
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          sideOffset={6}
          align="end"
          className="z-50 w-[236px] space-y-2 rounded-md border bg-popover p-2 text-popover-foreground shadow-md"
        >
          <p className="text-[11px] text-muted-foreground">
            Based on <span className="font-medium text-foreground">{token}</span>
          </p>

          <div className="grid grid-cols-2 gap-1.5">
            <Num label="size" value={next.size} min={1} onChange={(size) => patch({ size })} />
            <Num
              label="weight"
              value={next.weight}
              min={100}
              max={900}
              step={100}
              onChange={(weight) => patch({ weight })}
            />
            <Num
              label="line height"
              value={next.line_height}
              step={0.05}
              onChange={(line_height) => patch({ line_height })}
            />
            <Num
              label="tracking"
              value={next.letter_spacing ?? 0}
              min={-2}
              max={10}
              step={0.1}
              onChange={(letter_spacing) => patch({ letter_spacing: letter_spacing || undefined })}
            />
          </div>

          {/* A SEGMENTED CONTROL, not a native `<select>`. Two reasons, and the panel already made
              both: a native `<option>` cannot carry a glyph, which is the whole argument in
              `inspector-controls.tsx` for leaving native behind; and the three raw selects left in
              `ViewportToolbar` are already the one place the builder looks unfinished. `decorate` and
              `text case` in the Text section are the same shape — a short enum with an obvious icon —
              so this matches them rather than inventing a fourth idiom. */}
          <label className="flex items-center gap-2">
            <span className="w-[52px] shrink-0 text-[11px] text-muted-foreground">style</span>
            <ToggleGroup
              label="new style"
              value={next.style ?? 'normal'}
              options={STYLES}
              fill
              onChange={(style) => patch({ style: style === 'italic' ? 'italic' : undefined })}
            />
          </label>

          {/* WHAT YOU ARE ABOUT TO MAKE, in the same derived line the row itself shows. Reusing
              `summarizeThemeToken` is what stops the preview and the readout from ever disagreeing. */}
          <p className="truncate text-[10px] text-muted-foreground" title={summarizeThemeToken('fonts', next, theme)}>
            {summarizeThemeToken('fonts', next, theme)}
          </p>

          <button
            type="button"
            disabled={!changed}
            onClick={() => {
              onCreate(next);
              close();
            }}
            className="h-7 w-full rounded-md bg-primary text-[11px] font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            Create type style
          </button>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

function Num({
  label,
  value,
  onChange,
  ...props
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
} & Pick<React.InputHTMLAttributes<HTMLInputElement>, 'min' | 'max' | 'step'>): React.JSX.Element {
  return (
    <label className="min-w-0 space-y-0.5">
      <span className="block truncate text-[10px] text-muted-foreground">{label}</span>
      <input
        {...props}
        type="number"
        aria-label={`new type style ${label}`}
        value={value}
        onChange={(event) => event.target.value !== '' && onChange(Number(event.target.value))}
        className={cn(CONTROL, 'phq-number h-7 w-full text-[11px] tabular-nums')}
      />
    </label>
  );
}
